import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'

import { imageTextSquaredRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type ImageTextSquaredProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    media: MediaElement
    title: string
    paragraph: string
    alignment: 'left' | 'right'
    button: WPLink
  }>

const ImageTextSquared = ({
  media,
  title,
  paragraph,
  alignment,
  button,
  ...props
}: ImageTextSquaredProps) => {
  return (
    <Motion animation={imageTextSquaredRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <Grid className={cn('items-center !gap-x-[0rem]')}>
              <div
                className={cn(
                  'col-span-2 md:col-span-3 lg:col-span-6',
                  {
                    'md:order-2': alignment === 'right'
                  },
                  'flex h-full items-center border border-border-menu-light/30 dark:border-border-menu-dark/30',
                  'border-b-0 md:border',
                  {
                    'md:border-r-0': alignment === 'left',
                    'md:border-l-0': alignment === 'right'
                  }
                )}
              >
                <div
                  className={cn(
                    'grid gap-y-spacing-sm',
                    'p-spacing-sm md:p-spacing-xs xl:px-spacing-2xl xl:py-spacing-lg'
                  )}
                >
                  <div className="grid gap-y-spacing-lg">
                    {title ? (
                      <Typography variant="h4" component="h2" data-motion-title>
                        {title}
                      </Typography>
                    ) : null}

                    {paragraph ? (
                      <Typography
                        variant="large"
                        paragraph
                        data-motion-paragraph
                      >
                        {paragraph}
                      </Typography>
                    ) : null}
                  </div>

                  {button && button.url ? (
                    <div className="col-span-2">
                      <Button
                        className="py-spacing-xs"
                        asChild
                        variant="primary"
                        data-motion-button
                      >
                        <Link
                          href={button.url}
                          target={button.target}
                          scroll={false}
                        >
                          <TextEffect>
                            <Typography variant="regular">
                              {button.title}
                            </Typography>
                          </TextEffect>
                        </Link>
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
              <div
                className={cn('col-span-2 md:col-span-3 lg:col-span-6', {
                  'md:order-1': alignment === 'right'
                })}
              >
                <div className="relative aspect-square overflow-hidden xl:aspect-[620/393]">
                  <div
                    className="absolute left-0 top-0 h-full w-full bg-surface-accent-light"
                    data-motion-media
                  >
                    {button && button.url ? (
                      <Link
                        href={button.url}
                        target={button.target}
                        scroll={false}
                        className="block h-full w-full"
                      >
                        <Media fill {...media} />
                      </Link>
                    ) : (
                      <Media fill {...media} />
                    )}
                  </div>
                </div>
              </div>
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default ImageTextSquared
